/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/

import React from "react";
import { navigate } from "gatsby";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  CircularProgress,
  TextField as MuiTextField,
  IconButton as MuiIconButton,
  Tooltip,
  Typography,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ErrorOutline } from "@mui/icons-material";
import { keyframes } from "@mui/system";

import StartArrow from "./startarrow";

const useStyles = makeStyles()(() => ({
  containedButton: {
    display: "flex",
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 32,
    backgroundColor: "#65AFE9",
    color: "white",
  },
  outlinedButton: {
    display: "flex",
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 32,
    borderColor: "#1096ce",
    color: "#1096ce",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },
  textButton: {
    fontSize: 12,
    color: "purple",
    textDecoration: "underline",
    textDecorationColor: "purple",
    textTransform: "none",
    minHeight: 0,
    minWidth: 0,
    padding: 0,
  },
  textField: {
    width: "100%",
    borderRadius: 5,
  },
  selectBox: {
    width: 200,
    backgroundColor: "#FFF",
    marginBottom: 10,
  },
}));

const blink = keyframes`
  from {
    filter: brightness(80%);
    box-shadow: 0 0;
  }
  to {
    filter: brightness(120%);
    box-shadow: 0 0 10px #65AFE9;
  }
`;

interface ButtonProps {
  children: string | JSX.Element | JSX.Element[];
  onClick: () => void;
  aria?: string;
  name?: string;
  tooltip?: string;
  icon?: JSX.Element;
  endIcon?: JSX.Element;
  disabled?: boolean;
  loading?: boolean;
  blink?: boolean;
  color?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any;
}

export function ContainedButton(props: ButtonProps): JSX.Element {
  const { classes } = useStyles();
  const AnimatedButton = styled(Button)({
    animation: props.blink ? `${blink} 0.5s linear 0s infinite alternate` : "",
  });
  return (
    <AnimatedButton
      aria-label={props.aria || props.name}
      size="medium"
      variant="contained"
      data-cy={`${props.name || props.children}-btn`.toLowerCase()}
      className={classes.containedButton}
      startIcon={
        props.loading ? (
          <CircularProgress color="inherit" size={16} />
        ) : (
          props.icon
        )
      }
      endIcon={props.loading ? undefined : props.endIcon}
      disabled={props.disabled || props.loading}
      onClick={props.onClick}
      style={props.style}
    >
      {props.children}
    </AnimatedButton>
  );
}

export function OutlinedButton(props: ButtonProps): JSX.Element {
  const { classes } = useStyles();
  return (
    <Button
      aria-label={props.aria || props.name}
      size="medium"
      variant="outlined"
      data-cy={`${props.name || props.children}-btn`
        .toLowerCase()
        .replace(" ", "-")}
      className={classes.outlinedButton}
      startIcon={
        props.loading ? (
          <CircularProgress color="inherit" size={16} />
        ) : (
          props.icon
        )
      }
      endIcon={props.loading ? undefined : props.endIcon}
      disabled={props.disabled || props.loading}
      onClick={props.onClick}
      style={{
        color: props.color,
        borderColor: props.color,
        ...props.style,
      }}
    >
      {props.children}
    </Button>
  );
}

export function TextButton(props: ButtonProps): JSX.Element {
  const { classes } = useStyles();
  return (
    <Tooltip title={props.tooltip || ""}>
      <Button
        aria-label={props.aria || `${props.name || props.children} button`}
        size="medium"
        variant="text"
        data-cy={`${props.name || props.children}-btn`
          .toLowerCase()
          .replace(" ", "-")}
        endIcon={props.icon}
        className={classes.textButton}
        disabled={props.disabled}
        onClick={props.onClick}
        style={{
          color: props.color || "purple",
          textDecorationColor: props.color || "purple",
          ...props.style,
        }}
      >
        {props.children}
      </Button>
    </Tooltip>
  );
}

interface IconButtonProps extends ButtonProps {
  color?:
    | "inherit"
    | "default"
    | "error"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning";
}
export function MyIconButton(props: IconButtonProps): JSX.Element {
  return (
    <Tooltip title={props.tooltip || ""}>
      <span>
        <MuiIconButton
          aria-label={props.aria || props.name}
          disabled={props.disabled || props.loading}
          onClick={props.onClick}
          data-cy={`${props.name}-btn`.toLowerCase().replace(" ", "-")}
          color={props.color}
          style={props.style}
        >
          {props.loading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            props.children
          )}
        </MuiIconButton>
      </span>
    </Tooltip>
  );
}

export function StartButton(props: { lesson?: string }): JSX.Element {
  return (
    <ContainedButton
      aria="start lesson button"
      tooltip="At any time, click here to start the recommended lesson for your current topic."
      endIcon={<StartArrow />}
      disabled={!props.lesson}
      onClick={() => navigate(`/lesson?id=${props.lesson}`)}
    >
      Start
    </ContainedButton>
  );
}

export function TextField(props: {
  name?: string;
  aria?: string;
  value?: string;
  label?: string;
  type?: string;
  error?: boolean;
  helperText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.textField}>
      <MuiTextField
        inputProps={{
          "aria-label":
            props.aria || `${props.name || props.label} input field`,
          "aria-describedby": `${
            props.aria || props.name || props.label
          } helper text`,
        }}
        data-cy={`edit-${(props.name || props.label)?.toLowerCase()}`}
        data-test={props.value}
        margin="normal"
        variant="outlined"
        className={classes.textField}
        style={{
          backgroundColor: props.error ? "rgb(255, 245, 245)" : "white",
          ...props.style,
        }}
        type={props.type}
        label={props.label}
        value={props.value}
        error={props.error}
        onChange={props.onChange}
      />
      <div style={{ display: "flex", alignItems: "center", marginLeft: 5 }}>
        {props.error ? (
          <ErrorOutline
            style={{ color: "#d53737", fontSize: 12, marginRight: 5 }}
          />
        ) : undefined}
        <Typography
          id={`${props.aria || props.name || props.label} helper text`}
          style={{ fontSize: 12, color: props.error ? "#d53737" : "black" }}
        >
          {props.helperText}
        </Typography>
      </div>
    </div>
  );
}

export function DropDown(props: {
  aria?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  fullWidth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (val: any) => void;
  renderValue?: () => string;
  children: JSX.Element[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: Record<string, any>;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <FormControl fullWidth={props.fullWidth}>
      <InputLabel size="small">{props.label}</InputLabel>
      <Select
        inputProps={{
          "aria-label": props.aria || `${props.label} selection dropdown`,
        }}
        value={props.value}
        renderValue={props.renderValue}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(e) => props.onChange(e.target.value as any)}
        label={props.label}
        defaultValue={props.defaultValue}
        size="small"
        className={classes.selectBox}
        style={props.style}
      >
        {props.children?.map((item, i) =>
          React.cloneElement(props.children[i], {
            "aria-label": `${props.aria || props.label} item: ${
              item.key || (item.props ? item.props["value"] : i)
            }`,
          })
        )}
      </Select>
    </FormControl>
  );
}
